/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import IconProfile from '../../icons/IconProfile';
import IconArrowLeft from '../../icons/IconArrowLeft';
import IconClose from '../../icons/IconClose';
import ProfileAvatar from '../../ProfileAvatar';
import IconAppMenuStripes from '../../icons/IconAppMenuStripes';
import NotificationsButton from './NotificationsButton';

import CircleWithBorder from '../../CircleWithBorder';

import COLORS, { getFontColorClassNameOLD } from '../../../styles/Colors';
import IconBackpack from '../../icons/IconBackpack';
import { RightArrowWithCircle } from '../../ArrowWithCircle';
import { Icons } from '../../../../bphComponents/bundle';


const AppNavNextButton = (props) => {
  return (
    <div className="cmp-appnavnextbutton" onClick={props.isDisabled ? props.onDisabledClick : props.onClick}>
      {
        props.text && (
          <div className="button-text font-caption-1__STATIC font-color-gsLightest">
            <FormattedMessage id={props.text} />
          </div>
        )
      }
      <RightArrowWithCircle {...props} onClick={null} />
    </div>
  );
};
AppNavNextButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  onDisabledClick: PropTypes.func,
  isDisabled: PropTypes.bool,
  text: PropTypes.string,
  theme: PropTypes.string,
  direction: PropTypes.oneOf(['horizontal', 'down']),
};
AppNavNextButton.defaultProps = {
  direction: 'horizontal',
};

const AppNavPrevButton = (props) => {
  return (
    <div className="cmp-appnavprevbutton" onClick={props.isDisabled ? props.onDisabledClick : props.onClick}>
      <div className={classnames('button-icon', { 'is-disabled': props.isDisabled })}>
        <div className="touch-area-centering">
          <CircleWithBorder size={34} className="theme-group base stroke" fillColor="none" />

          <div className="cmp-appnavnextbutton-icon horizontal">
            <IconArrowLeft.Medium className="theme-group base fill" />
          </div>
        </div>
      </div>
    </div>
  );
};
AppNavPrevButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  onDisabledClick: PropTypes.func,
  isDisabled: PropTypes.bool,
};

const AppNavSubmitButton = (props) => {
  const overlayColor = props.isDisabled
    ? COLORS.disabledOverlayProfileColor
    : COLORS.enabledOverlayProfileColor;

  const checkmarkColor = COLORS.gsLightest;
  let circleColorClassName = '';
  let checkColorClassName = '';
  switch (props.theme) {
    case 'group':
      circleColorClassName = 'theme-group base background';
      checkColorClassName = 'fill-color-gsLightest';
      break;
    case 'profile':
    case 'backpack':
    default:
      circleColorClassName = '';
      checkColorClassName = '';
      break;
  }

  return (
    <div className="cmp-appnavsubmit" onClick={props.isDisabled ? props.onDisabledClick : props.onClick}>
      {
        props.text && (
          <div className="button-text font-caption-1__STATIC font-color-gsLightest">
            <FormattedMessage id={props.text} />
          </div>
        )
      }
      <div className="button-icon">
        <div className="touch-area-centering">
          <Icons.IconCheckmarkCircle.Large
            color={checkmarkColor}
            backgroundColor={overlayColor}
            circleClassName={circleColorClassName}
            checkClassName={checkColorClassName}
          />
        </div>

      </div>
    </div>
  );
};
AppNavSubmitButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  onDisabledClick: PropTypes.func,
  isDisabled: PropTypes.bool,
  text: PropTypes.string,
  theme: PropTypes.string,
};

const AppNavCancelButton = (props) => (
  <div className="cmp-appnavcancelbutton" onClick={props.onClick} style={props.style}>
    <IconClose.Small color={props.color} />

    <div className={classnames('font-caption-1__STATIC', getFontColorClassNameOLD(props.color))}>
      <FormattedMessage id="nav.cancel.button.label" />
    </div>
  </div>
);
AppNavCancelButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
  style: PropTypes.object,
};

const AppNavDoneButton = (props) => (
  <div className={classnames('cmp-appnavdonebutton is-clickable font-caption-1__STATIC', getFontColorClassNameOLD(props.color), { '__disabled': props.isDisabled })} disabled={props.isDisabled} onClick={props.isDisabled ? props.onDisabledClick : props.onClick}>
    <FormattedMessage id="nav.done.button.label" />
  </div>
);
AppNavDoneButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
  isDisabled: PropTypes.bool,
  onDisabledClick: PropTypes.func,
};

const AppNavEditButton = (props) => (
  <div className={classnames('cmp-appnavdonebutton is-clickable font-caption-1__STATIC', getFontColorClassNameOLD(props.color))} onClick={props.onClick}>
    <FormattedMessage id="nav.edit.button.label" />
  </div>
);
AppNavEditButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
};

const AppNavCloseButton = (props) => (
  <div className={classnames('cmp-appnavdonebutton is-clickable font-caption-1__STATIC', getFontColorClassNameOLD(props.color))} onClick={props.onClick}>
    <FormattedMessage id="nav.close.button.label" />
  </div>
);
AppNavCloseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
};

const AppNavCloseIconButton = (props) => (
  <button className="button-close" onClick={props.onClick} style={{ position: 'relative', ...props.style }}>
    <div
      className="button-close-background bkgd-color-dark-transparent-med-light border-color-gsMiddleLT"
      style={{ borderWidth: 1, borderStyle: 'solid', width: 32, height: 32, borderRadius: 16 }}
    />
    <div className="button-close-icon">
      <IconClose.Small color="white" />
    </div>
  </button>
);
AppNavCloseIconButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
  style: PropTypes.object,
};
AppNavCloseIconButton.defaultProps = {
  style: { left: 28, top: 4 },
};

const AppNavLogoutButton = (props) => (
  <div className={classnames('cmp-appnavlogoutbutton is-clickable font-caption-1__STATIC', getFontColorClassNameOLD(props.color))} onClick={props.onClick}>
    <FormattedMessage id="nav.logout.button.label" />
  </div>
);
AppNavLogoutButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
};

const AppNavShowMoreButton = (props) => (
  <div className="is-clickable" onClick={props.onClick}>
    {
      props.isActive ? <Icons.IconRevealDotsHorizontal.Medium color="white" /> : <Icons.IconRevealDotsEmptyHorizontal.Medium color="white" />
    }
  </div>
);
AppNavShowMoreButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
};

const AppNavBackButton = (props) => (
  <div className="cmp-appnavbackbutton" onClick={props.onClick}>
    <div>
      <IconArrowLeft.Medium color="white" />
    </div>
    <div className="font-caption-1__STATIC font-color-gsLightest">
      <FormattedMessage id="nav.back.button.label" />
    </div>
  </div>
);
AppNavBackButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const AppNavLoginButton = (props) => (
  <div className="cmp-appnavloginbutton" onClick={props.onClick}>
    <IconProfile.Small color={COLORS.white} />
    <div className="font-caption-1__STATIC font-color-gsLightest">
      <FormattedMessage id="signupEmail.login.button.label" />
    </div>
  </div>
);
AppNavLoginButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const AppNavSignUpButton = (props) => (
  <div className="cmp-appnavloginbutton" onClick={props.onClick}>
    <IconBackpack />
    <div className="font-caption-1__STATIC font-color-gsLightest">
      <FormattedMessage id="loginEmail.signup.button.label" />
    </div>
  </div>
);
AppNavSignUpButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const AppNavAvatarButton = (props) => (
  <div className="cmp-appnavavatarbutton" onClick={props.onClick}>
    <ProfileAvatar
      profile={props.profile}
      size={36}
      imgClassName="border-color-profile-highlight"
      isActive={false}
      isOutline
    />
    <IconAppMenuStripes />
  </div>
);
AppNavAvatarButton.propTypes = {
  profile: PropTypes.any,
  onClick: PropTypes.func.isRequired,
};

const AppNavNotificationOrAvatarButton = (props) => (
  <div className="cmp-appnavbutton-avatarnotification">
    <div className="cmp-appnavbutton-notification">
      <NotificationsButton />
    </div>
    <AppNavAvatarButton {...props} />
  </div>
);


const AppNavInviteButton = (props) => (
  <AppNavSubmitButton {...props} text="accessInviteUserEmail.invite.button.label" />
);

const AppNavAcceptButton = (props) => (
  <AppNavSubmitButton {...props} text="acceptOwnProfile.accept.button.label" />
);

const AppNavButtonPlaceholder = () => (<div className="cmp-appnavbutton-placeholder" />);

export {
  AppNavNextButton,
  AppNavPrevButton,
  AppNavSubmitButton,
  AppNavCancelButton,
  AppNavDoneButton,
  AppNavEditButton,
  AppNavCloseButton,
  AppNavLogoutButton,
  AppNavShowMoreButton,
  AppNavBackButton,
  AppNavLoginButton,
  AppNavSignUpButton,
  AppNavAvatarButton,
  AppNavNotificationOrAvatarButton,
  AppNavButtonPlaceholder,
  AppNavCloseIconButton,
  AppNavInviteButton,
  AppNavAcceptButton,
};
